// client/src/pages/Game/Battlefield/hooks/useGameElements.js

import { useState, useEffect, useRef } from 'react';

/**
 * Hook personnalisé pour gérer les positions et tailles des éléments du jeu.
 *
 * IMPORTANT: 
 * - Les POSITIONS sont en pourcentage (%) du frame — chaque axe indépendant.
 *   left: 50% = milieu horizontal, top: 70% = 70% de la hauteur.
 *   C'est correct car les positions sont relatives au container (le frame).
 *
 * - Les TAILLES sont en cqw (container query width).
 *   1cqw = 1% de la largeur du frame.
 *   width ET height utilisent cqw comme unité unique.
 *   Donc width=10, height=10 = carré parfait, quel que soit le viewport.
 *   Un cercle width=height + border-radius:50% reste circulaire.
 *
 * Le battlefield-frame a un ratio 9:16 (portrait) et utilise
 * container-type: size pour activer les unités cqw.
 */

// ── Positions par défaut (en pourcentages du frame) ──
// Calibrées par Julian — export elementum-layout.json du DevPanel (2026-08-16)
const defaultPositions = {
  // Main du joueur — bande horizontale en bas
  playerHand:       { x: 50, y: 98 },   // centré, tout en bas (export Julian 2026-08-23)
  opponentHand:     { x: 50, y: 2 },    // centré, tout en haut (export Julian 2026-08-23)

  // Zones de monstres — au-dessus/en-dessous du centre
  playerMonsters:   { x: 50, y: 70 },   // zone inférieure
  opponentMonsters: { x: 50, y: 29.9 }, // zone supérieure (export Julian 2026-08-23 (5))

  // Decks
  playerDeck:       { x: 9.99, y: 88.67 },
  opponentDeck:     { x: 90, y: 12 },

  // Points de vie + énergie
  playerLife:       { x: 86.42, y: 79.64 },
  opponentLife:     { x: 12, y: 20.35 },

  // Bouton d'action
  ActionButton:     { x: 50, y: 80 },

  // Zones de combat (entre monstres et centre)
  playerBattleZone:   { x: 50, y: 58 },
  opponentBattleZone: { x: 50, y: 42 },
};

// ── Tailles par défaut (en cqw = 1% de la largeur du frame) ──
// IMPORTANT: width et height utilisent la MÊME unité (cqw).
// Donc width=10, height=10 donne un carré parfait.
// Pour un cercle, mettre width=height et utiliser border-radius:50%.
const defaultSizes = {
  playerHand:       { width: 50, height: 40 },    // bande horizontale (export Julian 2026-08-23 (5))
  opponentHand:     { width: 70, height: 40 },
  playerMonsters:   { width: 75, height: 16 },    // zone des monstres
  opponentMonsters: { width: 75, height: 16 },
  playerDeck:       { width: 11, height: 15 },    // carte rectangulaire (2:3) (export (5))
  opponentDeck:     { width: 11, height: 15 },
  // CERCLE (export Julian 2026-08-23 (5)) — perles positionnées individuellement
  playerLife:       { width: 11, height: 11, crystalSize: 3.25, crystalRingGap: 0.65, pearlSize: 3.8, pearlGap: 5, pearlSpacing: 0.4,
    gems: { 'mp-0': { x: 10.04, y: -10.81 }, 'mp-1': { x: 6.56, y: -14.78 }, 'mp-2': { x: 6.56, y: -20.07 } } },
  opponentLife:     { width: 11, height: 11, crystalSize: 3.25, crystalRingGap: 0.65, pearlSize: 3.8, pearlGap: 5, pearlSpacing: 0.4,
    gems: { 'mp-0': { x: -8.42, y: 10.46 }, 'mp-1': { x: -5.01, y: 14.95 }, 'mp-2': { x: -5.04, y: 20.3 } } },
  ActionButton:     { width: 20, height: 8 },     // bouton rectangulaire
  playerBattleZone:   { width: 75, height: 16 },  // cartes même taille que zone monstres
  opponentBattleZone: { width: 75, height: 16 },
};

// Échelles de police individuelles par catégorie de texte
// Calibrées par Julian — export elementum-layout.json (2026-08-16)
const defaultFontScales = {
  life: 1.9,      // chiffres de PV dans les cercles
  monster: 2.7,   // stats ATK/DEF des monstres
  cost: 3,        // coûts des cartes en main
  deck: 3,        // compteurs de cartes du deck
  action: 2.45,   // texte du bouton d'action
  zone: 2.25,     // textes des zones de combat
  gem: 3,         // gemmes/cristaux d'énergie
};

const useGameElements = () => {
  const [elementPositions, setElementPositions] = useState({ ...defaultPositions });
  const [elementSizes, setElementSizes] = useState({ ...defaultSizes });
  const [configLoaded, setConfigLoaded] = useState(false);

  // ── Échelle de police globale (multiplicateur maître) ──
  const [fontScale, setFontScale] = useState(1.0);

  // ── Échelles de police individuelles par catégorie ──
  const [fontScales, setFontScales] = useState({ ...defaultFontScales });

  // Référence vers le frame pour permettre au mode dev de convertir
  // les déplacements en pixels vers des pourcentages
  const frameRef = useRef(null);

  useEffect(() => {
    // Charger depuis localStorage si disponible
    try {
      const savedPositions = localStorage.getItem('elementPositions_pct');
      const savedSizes = localStorage.getItem('elementSizes_pct');

      // Version du format — incrémenter quand le format change pour forcer un reset
      const FORMAT_VERSION = 'v6_battle';
      const savedVersion = localStorage.getItem('elementFormatVersion');

      if (savedVersion !== FORMAT_VERSION) {
        // Format obsolète (ou nouvelle install) — nettoyer et utiliser les defaults
        localStorage.removeItem('elementPositions_pct');
        localStorage.removeItem('elementSizes_pct');
        localStorage.setItem('elementFormatVersion', FORMAT_VERSION);
      } else {
        if (savedPositions) {
          const parsed = JSON.parse(savedPositions);
          setElementPositions(prev => ({ ...prev, ...parsed }));
        }
        if (savedSizes) {
          const parsed = JSON.parse(savedSizes);
          setElementSizes(prev => ({ ...prev, ...parsed }));
        }
      }

      // Charger les échelles de police
      try {
        const savedFontScale = localStorage.getItem('fontScale');
        if (savedFontScale) {
          setFontScale(parseFloat(savedFontScale));
        }
        const savedFontScales = localStorage.getItem('fontScales');
        if (savedFontScales) {
          const parsed = JSON.parse(savedFontScales);
          setFontScales(prev => ({ ...prev, ...parsed }));
        }
      } catch (e) { /* ignore */ }
    } catch (e) {
      console.warn('useGameElements: erreur lecture localStorage', e);
    }

    setConfigLoaded(true);
  }, []);

  // Sauvegarder dans localStorage
  const saveToLocal = (positions, sizes) => {
    try {
      localStorage.setItem('elementPositions_pct', JSON.stringify(positions));
      localStorage.setItem('elementSizes_pct', JSON.stringify(sizes));
    } catch (e) {
      console.warn('useGameElements: erreur sauvegarde localStorage', e);
    }
  };

  // Mettre à jour l'échelle de police globale (multiplicateur maître)
  const updateGlobalFontScale = (scale) => {
    const clamped = Math.max(0.5, Math.min(3.0, parseFloat(scale) || 1.0));
    setFontScale(clamped);
    try {
      localStorage.setItem('fontScale', String(clamped));
    } catch (e) { /* ignore */ }
  };

  // Mettre à jour l'échelle de police d'une catégorie individuelle
  const updateElementFontScale = (key, value) => {
    const clamped = Math.max(0.5, Math.min(3.0, parseFloat(value) || 1.0));
    setFontScales(prev => {
      const next = { ...prev, [key]: clamped };
      try {
        localStorage.setItem('fontScales', JSON.stringify(next));
      } catch (e) { /* ignore */ }
      return next;
    });
  };

  // Réinitialiser toutes les échelles de police
  const resetFontScales = () => {
    setFontScale(1.0);
    setFontScales({ ...defaultFontScales });
    try {
      localStorage.removeItem('fontScale');
      localStorage.removeItem('fontScales');
    } catch (e) { /* ignore */ }
  };

  return {
    elementPositions,
    setElementPositions,
    elementSizes,
    setElementSizes,
    configLoaded,
    defaultPositions,
    defaultSizes,
    frameRef,
    saveToLocal,
    fontScale,
    updateGlobalFontScale,
    fontScales,
    updateElementFontScale,
    resetFontScales,
  };
};

export default useGameElements;
